// @flow
import * as React from 'react';
import { useMemo, useState } from 'react';
import {
  darkenHexColor,
  DataGrid,
  DataGridColumn,
  letterToColorfulHex,
  lightenHexColor,
  ListPageWrapper,
  PageTitle,
  useSessionStorageState,
  useSetDefaultAgentMutation
} from '../../shared';
import { Avatar, Box, CircularProgress, Divider, IconButton, MenuItem, Stack, Typography } from '@mui/material';
import { formatToPhoneNumber, SelectInput } from '@coverright/ui/inputs';
import { getUserName } from '@coverright/utils';
import _ from 'lodash';
import { Check, X } from '@phosphor-icons/react';
import { Agent, useAgents } from '../attempted-leads/hooks/use-agents';
import { EditAgentModal } from './ui/edit-agent-modal';
import { CreateAgentButton } from './ui/create-agent-button';
import { useConfirm } from '@coverright/features';
import { theme } from '../../app/theme';
import { enqueueSnackbar } from 'notistack';
import { TableSettings, useTableSettings, withTableSettings } from '../../features';
import { AgentField, PageInput, SortDirection } from '@coverright/data-access/types/enrollment';

const Page = () => {
  const [page, setPage] = usePage();
  const [sort, setSort] = useSort();
  const [enabled, setEnabled] = useEnabled();

  const {agents, loading, total, refetch} = useAgents(page, enabled, sort);

  const filters = useMemo(() => ({enabled}), [enabled])

  const {columns} = useTableSettings(tableColumns, filters, settings => settings.filters && setEnabled(settings.filters.enabled));

  return (
    <ListPageWrapper>
      <PageTitle title="Agent list">
        <TableSettings />

        <Stack gap={2} direction={'row'}>
          <SelectInput placeholder={'Enabled'}
                       className={'mt-0 w-200'}
                       value={enabled}
                       onChange={event => {
                         setEnabled(event.target.value as string);
                       }}
                       hideTick
          >
            <MenuItem value={''}>All</MenuItem>
            <MenuItem value={'true'}>Enabled</MenuItem>
            <MenuItem value={'false'}>Disabled</MenuItem>
          </SelectInput>
          <Divider orientation={'vertical'} />
          <CreateAgentButton onSubmit={refetch} />
        </Stack>

      </PageTitle>

      <DataGrid columns={columns}
                page={page.page}
                rowsPerPage={page.size}
                total={total}
                loading={loading}
                sort={sort}
                onSortModelChange={setSort}
                onPageChange={p => setPage(prev => ({...prev, page: p }))}
                onRowsPerPageChange={p => setPage(prev => ({...prev, size: p }))}
                rows={agents}/>
    </ListPageWrapper>
  );
};

export const Agents = withTableSettings(Page, 'agents');

const tableColumns: DataGridColumn<Agent, AgentField>[] = [
  {
    title: 'Name',
    value: v => <AgentName agent={v} />,
    sortKey: AgentField.Name
  },
  {
    title: 'Email',
    value: v => v.email,
    sortKey: AgentField.Email
  },
  {
    title: 'Phone',
    value: v => formatToPhoneNumber(v.phone),
  },
  {
    title: 'Enabled',
    value: v => v.enabled ? <Check weight={'bold'} color={'green'} size={14} /> : <X weight={'bold'} color={'red'} size={14} />,
    sortKey: AgentField.Enabled
  },
  {
    title: 'Receive emails',
    value: v => v.receiveEmails ? <Check weight={'bold'} color={'green'} size={14} /> : <X weight={'bold'} color={'red'} size={14} />
  },
  {
    title: 'In Round Robin',
    value: v => typeof v.gmail === 'boolean' ? (
        v.enabledForRoundRobin ? <Check weight={'bold'} color={'green'} size={14} /> : <X weight={'bold'} color={'red'} size={14} />
      ) : null
  },
  {
    title: 'Gmail connected',
    value: v => typeof v.gmail === 'boolean' ? (
        v.gmail ? <Check weight={'bold'} color={'green'} size={14} /> : <X weight={'bold'} color={'red'} size={14} />
      ) : null
  },
  {
    title: 'Is default',
    value: v => <IsDefaultToggle isDefault={v.isDefault} agentId={v.id} />
  },
]

const AgentName = ({agent}: {agent: Agent}) => {
  const [open, setOpen] = useState(false);
  return <>
    <EditAgentModal agent={agent} open={open} onClose={() => setOpen(false)} />
    <Stack direction={'row'} alignItems={'center'} gap={1}>
      <Avatar sx={{ bgcolor: lightenHexColor(letterToColorfulHex(agent.email || getUserName(agent)), 50), color: darkenHexColor(letterToColorfulHex(agent.email || getUserName(agent)), 70), width: 20, height: 20, fontSize: 12 }}>
        {agent.firstName && _.toUpper(agent.firstName[0])}
      </Avatar>
      <Typography variant={'body2'} fontWeight={500} sx={{cursor: 'pointer'}} onClick={() => setOpen(true)}>
        {getUserName(agent)}
      </Typography>
    </Stack>
  </>
}

const IsDefaultToggle = ({isDefault, agentId}: {isDefault?: boolean, agentId: string}) => {
  const [setDefaultAgent, {loading}] = useSetDefaultAgentMutation({
    onCompleted: data => {
      enqueueSnackbar('Changes saved!', { variant: 'success' })
    },
    refetchQueries: ['agents']
  });
  const confirm = useConfirm(theme);

  const handleClick = () => {
    confirm({
      content: 'Are you sure you want to set this agent as default?',
      okButtonTitle: 'Yes',
      cancelButtonTitle: 'No',
      title: 'Attention!',
    })
      .then(val => {
        if (val) {
          setDefaultAgent({
            variables: {agentId}
          })
        }
      })
  }

  return (
    isDefault ?
      <Box p={.5}><Check weight={'bold'} color={'green'} size={14} /></Box> :
      <IconButton size={'small'} onClick={handleClick}>
        {!loading ? <X weight={'bold'} color={'red'} size={14} /> : <CircularProgress size={14} />}
      </IconButton>
  )
}

const useEnabled = () => {
  return useSessionStorageState<string>(
    'agentEnabled',
    ''
  );
};

const usePage = () => {
  return useSessionStorageState<PageInput>(
    'agentListPage',
    { page: 0, size: 20 }
  );
};

const useSort = () => {
  return useSessionStorageState<{field: AgentField, direction: SortDirection} | undefined>(
    'agentListSort',
    {field: AgentField.Name, direction: SortDirection.Asc}
  )
}
